import { normaliseSourcing, searchModeForSourcing } from "utils/fieldSourcing";

// Field grouping + display labels for the AI carry-forward review page.
//
// These are presentation concerns: the ingestion payload is a flat dict of
// { fieldKey: { value, confidence, source_locations, reasoning } } with no
// section grouping and no display labels (the backend's Pydantic field
// descriptions are schema-only and never serialized), so grouping + labels are
// defined here in the frontend. Keep each grouping's keys a subset of the field
// set the consumer can actually carry into a submission.

export const FIELD_LABELS = {
  insured: "Insured name",
  insuredAddress: "Mailing address",
  industryClass: "Industry class",
  naics: "NAICS",
  categoryCode: "Category code",
  riskDescription: "Risk description",
  riskSummary: "Risk summary",
  inception: "Effective date",
  expiration: "Expiration date",
  reference: "Reference",
  currency: "Currency",
  paymentTerms: "Payment terms",
  notes: "Notes",
  brokerage: "Brokerage",
  profit: "Profit",
  maximumPossibleLoss: "Maximum possible loss",
};

// Default grouping for regular AI ingestion (the upload drawer). Superset of the
// fields extractBestFields can map into program state.
export const REVIEW_SECTIONS = [
  {
    name: "Insured",
    keys: [
      "insured",
      "insuredAddress",
      "industryClass",
      "naics",
      "categoryCode",
      "riskDescription",
      "riskSummary",
    ],
  },
  {
    name: "Policy",
    keys: ["inception", "expiration", "reference", "currency", "notes"],
  },
  { name: "Financials", keys: ["brokerage", "profit"] },
];

// Triage carry-forward — only the fields triage maps into a submission
// (must stay a subset of CARRY_FORWARD_FIELDS in TriagePipeline.js).
export const TRIAGE_REVIEW_SECTIONS = [
  {
    name: "Insured",
    keys: [
      "insured",
      "insuredAddress",
      "industryClass",
      "naics",
      "riskDescription",
    ],
  },
  {
    name: "Policy",
    keys: ["inception", "expiration", "reference", "currency"],
  },
  { name: "Financials", keys: ["brokerage", "maximumPossibleLoss"] },
];

// The fields with built-in search descriptions (mirrors AGENT_SEARCHABLE_FIELDS in
// services/ai_agents/research_agent/agent_search_fields.py). Not a gate: any rule field
// may be web-sourced — the agent answers "not_found" for what the web cannot know.
export const AGENT_SEARCHABLE_KEYS = [
  "insured",
  "insuredAddress",
  "naics",
  "industryClass",
];

// Triage rules, ingestion fields, the search agent, and program state share one
// vocabulary — a rule on "insured" reads the "insured" ingestion field and lands on
// program.insured (pinned to the backend by test_field_vocabulary_is_unified).

/** The rule's field key, or null for a rule with no field name. */
export const ingestionKeyForRuleField = (fieldName) => fieldName || null;

/**
 * Collapse a ruleset's rules into {ingestionKey: sourcing}, walking combined rules.
 *
 * Sourcing is set per rule component, at any nesting depth; every named field is
 * included. First setting wins if two rules name the same field.
 */
export const sourcingByIngestionKey = (rules) => {
  const byKey = {};
  const walk = (entries) => {
    (entries || []).forEach((entry) => {
      if (entry?.kind === "group") {
        walk(entry.rules);
        return;
      }
      const key = ingestionKeyForRuleField(entry?.field_name);
      if (key && !(key in byKey))
        byKey[key] = normaliseSourcing(entry?.sourcing);
    });
  };
  walk(rules);
  return byKey;
};

/**
 * The subset of {ingestionKey: sourcing} that needs a web search, as {key: mode}.
 *
 * The mode is derived from the sourcing rather than stored, so there is one place that
 * decides whether a field is compared or simply sourced.
 */
/**
 * Rule descriptions by field key, walking combined rules — the search agent's brief for
 * fields it has no built-in notion of.
 */
export const ruleDescriptionsByIngestionKey = (rules) => {
  const byKey = {};
  const walk = (entries) => {
    (entries || []).forEach((entry) => {
      if (entry?.kind === "group") {
        walk(entry.rules);
        return;
      }
      const key = ingestionKeyForRuleField(entry?.field_name);
      if (key && entry?.description && !(key in byKey))
        byKey[key] = entry.description;
    });
  };
  walk(rules);
  return byKey;
};

export const agentSearchModesForRules = (rules) => {
  const modes = {};
  Object.entries(sourcingByIngestionKey(rules)).forEach(([key, sourcing]) => {
    const mode = searchModeForSourcing(sourcing);
    if (mode !== "off") modes[key] = mode;
  });
  return modes;
};
